// =====================================================
// 白板系统 - 类型定义
// =====================================================

/** 白板项目（Supabase 数据库记录） */
export interface WhiteboardProject {
  id: string;
  name: string;
  description?: string;
  /** Supabase Storage 路径前缀: {userId}/{projectId} */
  storage_key: string;
  /** 画布元素数据（elements + appState） */
  canvas_data?: Record<string, any>;
  /** 缩略图 URL（可选） */
  thumbnail_url?: string;
  created_by?: string;
  updated_by?: string;
  created_at: string;
  updated_at: string;
  /** 协作分享令牌 */
  share_token?: string | null;
  /** 分享模式：private = 仅自己, edit = 允许协作 */
  share_mode?: 'private' | 'edit';
  /** 画布持久化版本，用于拒绝过期覆盖 */
  canvas_version?: number;
}

export interface WhiteboardCollaboratorMember {
  userId: string;
  name: string;
  email: string;
  avatarUrl?: string;
  joinedAt: string;
  pinnedAt?: string | null;
  isOwner: boolean;
}

export interface WhiteboardAccessState {
  hasAccess: boolean;
  isPinned: boolean;
  isOwner: boolean;
  reason: 'owner' | 'member' | 'closed' | 'removed' | 'not_found' | 'unauthenticated';
}

export type CollaborationEndedReason = 'closed' | 'removed';

/** 创建白板参数 */
export interface CreateWhiteboardParams {
  name?: string;
  description?: string;
}

/** 更新白板参数 */
export interface UpdateWhiteboardParams {
  name?: string;
  description?: string;
  thumbnail_url?: string;
  canvas_data?: Record<string, any>;
}

/** 保存状态 */
export type SaveStatus = 'saved' | 'saving' | 'unsaved' | 'error';

// =====================================================
// 协作相关类型
// =====================================================

/** 协作用户信息（Presence 中追踪） */
export interface CollabUser {
  userId: string;
  username: string;
  email: string;
  avatarUrl?: string;
  /** 分配的光标颜色（hex） */
  color: string;
  /** 光标位置 */
  pointer?: { x: number; y: number; tool?: 'pointer' | 'laser' };
  button?: 'up' | 'down';
  isCurrentUser: boolean;
  idleState?: 'active' | 'idle' | 'away';
  /** 选中的元素 ID */
  selectedElementIds?: Record<string, boolean>;
}

/** 协作者预设颜色板 */
export const COLLAB_COLORS = [
  '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4',
  '#FFEAA7', '#DDA0DD', '#98D8C8', '#F7DC6F',
  '#BB8FCE', '#85C1E9', '#F0B27A', '#82E0AA',
] as const;

/** Realtime 协作连接状态 */
export type CollabConnectionStatus =
  | 'disconnected'
  | 'connecting'
  | 'connected'
  | 'reconnecting'
  | 'error';

/** 白板 Store 状态 */
export interface WhiteboardState {
  projects: WhiteboardProject[];
  collabProjects: WhiteboardProject[];
  isLoadingProjects: boolean;
  currentProject: WhiteboardProject | null;
  isLoadingCanvas: boolean;
  isSidebarOpen: boolean;

  // 未保存状态（编辑器设置，侧边栏读取）
  hasUnsavedChanges: boolean;
  setHasUnsavedChanges: (v: boolean) => void;
  /** 编辑器注册的保存回调，侧边栏在切换前调用 */
  saveCurrentCanvas: (() => Promise<void>) | null;
  setSaveCurrentCanvas: (fn: (() => Promise<void>) | null) => void;
  /** 编辑器注册的导出数据回调，返回完整元素+图片 */
  getCanvasExportData: (() => { elements: any[]; appState: any; files: Record<string, any> }) | null;
  setGetCanvasExportData: (fn: (() => { elements: any[]; appState: any; files: Record<string, any> }) | null) => void;

  loadProjects: () => Promise<void>;
  openProject: (id: string, shareToken?: string | null) => Promise<void>;
  createProject: (params?: CreateWhiteboardParams) => Promise<WhiteboardProject>;
  updateProjectMeta: (id: string, params: UpdateWhiteboardParams) => Promise<void>;
  deleteProject: (id: string) => Promise<void>;
  duplicateProject: (id: string) => Promise<WhiteboardProject>;
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
}
